<script lang="ts">
  import ArrowDown from '@lucide/svelte/icons/arrow-down';
  import ArrowUp from '@lucide/svelte/icons/arrow-up';
  import Plus from '@lucide/svelte/icons/plus';
  import X from '@lucide/svelte/icons/x';

  import { getServices } from '../../app/context';
  import { addCategory, type NewRole, renameCategory, setCategoryRoles } from '../../model/ops';
  import { normalizeName } from '../../model/text';
  import type { Category } from '../../model/types';
  import { LIMITS } from '../../model/types';
  import { format, strings } from '../../strings';
  import Button from '../../ui/Button.svelte';
  import Dialog from '../../ui/Dialog.svelte';
  import IconButton from '../../ui/IconButton.svelte';

  interface Props {
    open?: boolean;
    /** The category to edit; omit to create a new one. */
    category?: Category | null;
    returnFocusTo?: HTMLElement | null;
    onclose?: () => void;
  }

  let { open = $bindable(false), category = null, returnFocusTo, onclose }: Props = $props();

  const { store } = getServices();
  const id = $props.id();
  let name = $state('');
  let roles: NewRole[] = $state([]);

  $effect(() => {
    if (!open) return;
    name = category?.name ?? '';
    roles = category
      ? category.roles.map((role) => ({ ...role }))
      : [{ id: store.newId(), name: strings.defaultRole }];
  });

  const valid = $derived(
    normalizeName(name) !== null && roles.every((role) => normalizeName(role.name) !== null),
  );

  function swap(i: number, j: number) {
    const next = [...roles];
    const [a, b] = [next[i], next[j]];
    if (!a || !b) return;
    next[i] = b;
    next[j] = a;
    roles = next;
  }

  function save() {
    if (!valid) return;
    const draft = $state.snapshot(roles);
    if (category) {
      const categoryId = category.id;
      store.apply((doc) =>
        setCategoryRoles(renameCategory(doc, categoryId, name), categoryId, draft),
      );
    } else {
      store.apply((doc) => addCategory(doc, { id: store.newId(), name, roles: draft }));
    }
    open = false;
  }
</script>

<Dialog
  bind:open
  title={category ? strings.editCategoryTitle : strings.addCategoryTitle}
  {returnFocusTo}
  {onclose}
>
  <form
    id="{id}-form"
    class="flex flex-col gap-4"
    onsubmit={(event) => {
      event.preventDefault();
      save();
    }}
  >
    <div class="flex flex-col gap-1">
      <label for="{id}-name" class="text-sm font-medium">{strings.categoryName}</label>
      <!-- svelte-ignore a11y_autofocus (the dialog exists to fill this in) -->
      <input
        id="{id}-name"
        bind:value={name}
        required
        autofocus
        maxlength={LIMITS.name}
        class="min-h-10 rounded-lg border border-line-strong/70 bg-surface px-3 text-base text-ink sm:text-sm"
      />
    </div>
    <fieldset class="flex flex-col gap-2">
      <legend class="text-sm font-medium">{strings.roles}</legend>
      <p class="text-xs text-ink-muted">{strings.rolesHint}</p>
      <ol class="flex flex-col gap-2">
        {#each roles as role, i (role.id)}
          <li class="flex items-center gap-1">
            <input
              bind:value={role.name}
              aria-label="{strings.roleName} {i + 1}"
              required
              maxlength={LIMITS.name}
              class="min-h-10 min-w-0 flex-1 rounded-lg border border-line-strong/70 bg-surface px-3 text-base text-ink sm:text-sm"
            />
            <IconButton
              size="sm"
              icon={ArrowUp}
              label={format.moveUp(role.name)}
              disabled={i === 0}
              onclick={() => {
                swap(i, i - 1);
              }}
            />
            <IconButton
              size="sm"
              icon={ArrowDown}
              label={format.moveDown(role.name)}
              disabled={i === roles.length - 1}
              onclick={() => {
                swap(i, i + 1);
              }}
            />
            <IconButton
              size="sm"
              icon={X}
              label={format.removeRole(role.name)}
              disabled={roles.length <= LIMITS.minRoles}
              onclick={() => {
                roles = roles.filter((r) => r.id !== role.id);
              }}
            />
          </li>
        {/each}
      </ol>
      <Button
        size="sm"
        variant="ghost"
        class="self-start"
        disabled={roles.length >= LIMITS.maxRoles}
        onclick={() => {
          roles = [...roles, { id: store.newId(), name: '' }];
        }}
      >
        <Plus size={16} aria-hidden="true" />
        {strings.addRole}
      </Button>
    </fieldset>
  </form>
  {#snippet footer()}
    <Button onclick={() => (open = false)}>{strings.cancel}</Button>
    <Button variant="primary" type="submit" form="{id}-form" disabled={!valid}>
      {strings.save}
    </Button>
  {/snippet}
</Dialog>
